import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Input } from 'components/ui/input';
import type { DayRefusal } from 'utils/interventionDate';
import { parisToday } from 'utils/interventionDate';
import { SEGMENTED, segmentClass } from 'components/ui/segmented';
import { interventionDay } from './upcomingAssignment';

const ERROR = 'text-[12.5px] text-rose-600 dark:text-rose-400';

export interface DayWindow {
  earliest: string;
  deadline: string;
}

interface DayWindowFieldProps extends DayWindow {
  onChange: (window: DayWindow) => void;
  /** A technician accepted (E14): read out, never editable. */
  locked: boolean;
  refusal: DayRefusal | null;
  startRefused: boolean;
  orderRefused: boolean;
  /** « Set the deadline before sending. » only while a send is still possible. */
  askDeadline: boolean;
}

type Mode = 'range' | 'exact';

/** F14 — « Plage de jours | Jour précis ». Exact day is `earliest = deadline`, derived, never stored. */
const DayWindowField = ({
  earliest,
  deadline,
  onChange,
  locked,
  refusal,
  startRefused,
  orderRefused,
  askDeadline,
}: DayWindowFieldProps) => {
  const { t } = useTranslation();
  // The saved window picks the mode until the first click; typing never flips it afterwards (LS-2206).
  const [choice, setChoice] = useState<Mode | null>(null);
  const mode: Mode = choice ?? (earliest !== '' && earliest === deadline ? 'exact' : 'range');
  const rangeStart = useRef('');

  if (locked) {
    const window =
      earliest && earliest !== deadline
        ? t('Between {{start}} and {{end}}', { start: interventionDay(earliest), end: interventionDay(deadline) })
        : interventionDay(deadline) ?? '—';
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium">{t('Deadline')}</span>
        <p className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 text-[13px]">
          <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          {window}
        </p>
        <p className="text-[12.5px] text-muted-foreground" role="status">
          {t('Locked: a technician has accepted the mission.')}
        </p>
        <p className="text-[12px] text-muted-foreground">{t('The tariff and useful information remain editable.')}</p>
      </div>
    );
  }

  const editRange = (window: DayWindow) => {
    setChoice('range');
    onChange(window);
  };
  const pick = (next: Mode) => {
    if (mode === 'range') rangeStart.current = earliest;
    setChoice(next);
    onChange({ earliest: next === 'exact' ? deadline : rangeStart.current, deadline });
  };
  const deadlineInvalid = refusal !== null || orderRefused;

  return (
    <div className="flex flex-col gap-2">
      <fieldset className={SEGMENTED}>
        <legend className="sr-only">{t('Deadline')}</legend>
        {(['range', 'exact'] as const).map((option) => (
          <label key={option} className={segmentClass(mode === option)}>
            <input
              type="radio"
              name="assign-day-window"
              className="sr-only"
              checked={mode === option}
              onChange={() => pick(option)}
            />
            {option === 'range' ? t('Day range') : t('Exact day')}
          </label>
        ))}
      </fieldset>

      {mode === 'range' ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="assign-earliest-date" className="text-[13px] font-medium">
              {t('From')}
            </label>
            <Input
              id="assign-earliest-date"
              type="date"
              min={parisToday()}
              value={earliest}
              aria-invalid={startRefused}
              onChange={(event) => editRange({ earliest: event.target.value, deadline })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="assign-intervention-date" className="text-[13px] font-medium">
              {t('Due by')} <span className="text-rose-600 dark:text-rose-400">*</span>
            </label>
            <Input
              id="assign-intervention-date"
              type="date"
              min={earliest || parisToday()}
              value={deadline}
              aria-invalid={deadlineInvalid}
              aria-describedby="assign-deadline-hint"
              onChange={(event) => editRange({ earliest, deadline: event.target.value })}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="assign-intervention-date" className="text-[13px] font-medium">
            {t('Intervention day')} <span className="text-rose-600 dark:text-rose-400">*</span>
          </label>
          <Input
            id="assign-intervention-date"
            type="date"
            min={parisToday()}
            value={deadline}
            aria-invalid={deadlineInvalid}
            aria-describedby="assign-deadline-hint"
            onChange={(event) => {
              setChoice('exact');
              onChange({ earliest: event.target.value, deadline: event.target.value });
            }}
          />
        </div>
      )}
      <p id="assign-deadline-hint" className="text-[12px] text-muted-foreground">
        {mode === 'range'
          ? t('The technician will choose their day within this range.')
          : t('The technician will have no other possible day.')}
      </p>

      {refusal === 'empty' && askDeadline && (
        <p className={ERROR} role="alert">
          {t('Set the deadline before sending.')}
        </p>
      )}
      {refusal === 'past' && (
        <p className={ERROR} role="alert">
          {t('The deadline must be today or later.')}
        </p>
      )}
      {refusal === 'malformed' && (
        <p className={ERROR} role="alert">
          {t('This is not a real calendar day. Check the day and the month.')}
        </p>
      )}
      {startRefused && (
        <p className={ERROR} role="alert">
          {t('The start date must be today or later.')}
        </p>
      )}
      {orderRefused && (
        <p className={ERROR} role="alert">
          {t('The deadline must be the same day as, or after, the start date.')}
        </p>
      )}
    </div>
  );
};

export default DayWindowField;
